import { Float } from "@headlessui-float/react";
import { Combobox } from "@headlessui/react";
import {
  CheckIcon,
  ChevronUpDownIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import classNames from "classnames";
import React, { Fragment, ReactNode, useState } from "react";

type NidComboboxProps<T> = {
  /**
   * The available values in the combobox
   */
  values: T[];
  /**
   * Gets the search text from a given value to filter down the combobox values (optional)
   * @param Value The original value
   * @returns A string that can be searched for
   */
  getSearchText?: (value: T) => string;
  /**
   * Transforms a given value to a string that gets displayed in the combobox (optional)
   * @param value The original value
   * @returns A string that can be displayed
   */
  getDisplayText?: (value: T) => string;
  placeholder?: string;
  compareBy?: (keyof T & string) | ((a: T, b: T) => boolean);
  children?: ReactNode;
  onSearchTextChanged?: (searchText: string) => void;
  className?: string;
};

export type NidComboboxMultipleProps<T> = {
  /**
   * Notifies the observer when the selected value changes
   * @param value The newly selected value or values
   */
  onValueChange?: (value: T[] | undefined) => void;
  /**
   * The selected value or values
   */
  value?: T[];
} & NidComboboxProps<T>;

export type NidComboboxSingleProps<T> = {
  /**
   * Notifies the observer when the selected value changes
   * @param value The newly selected value or values
   */
  onValueChange?: (value: T | undefined) => void;
  /**
   * The selected value or values
   */
  value?: T;
} & NidComboboxProps<T>;

function NidComboboxOptions<T>({
  values,
  getSearchText = (value: T) => value as string,
  getDisplayText = (value: T) => value as string,
  query,
  setQuery,
  children,
}: NidComboboxProps<T> & {
  query: string;
  setQuery: (query: string) => void;
}) {
  const filteredValues =
    query === ""
      ? values
      : values.filter((value) =>
          getSearchText(value)
            .toLowerCase()
            .replace(/\s+/g, "")
            .includes(query.toLowerCase().replace(/\s+/g, "")),
        );

  return (
    <Fragment>
      {React.Children.map(children, (child) => {
        if (
          React.isValidElement(child) &&
          child.type === NidComboboxOptions.OptionsPrefix
        ) {
          return React.cloneElement(child);
        }
      })}

      {filteredValues.length === 0 && query !== "" ? (
        <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
          Nothing found.
        </div>
      ) : (
        filteredValues.map((value, i) => (
          <Combobox.Option
            key={`${getDisplayText(value)}_${i}`}
            className={({ active }) =>
              `relative cursor-default select-none py-2 pl-10 pr-4 ${
                active ? "bg-th-primary-600 text-white" : "text-gray-900"
              }`
            }
            value={value}
            data-cy="combobox-option"
          >
            {({ selected, active }) => (
              <>
                <span
                  className={`block truncate ${
                    selected ? "font-medium" : "font-normal"
                  }`}
                >
                  {getDisplayText(value)}
                </span>
                {selected ? (
                  <span
                    className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                      active ? "text-white" : "text-th-primary-600"
                    }`}
                  >
                    <CheckIcon className="h-5 w-5" aria-hidden="true" />
                  </span>
                ) : null}
              </>
            )}
          </Combobox.Option>
        ))
      )}
    </Fragment>
  );
}

function NidComboboxSingle<T>({
  value,
  values,
  getSearchText = (value: T) => value as string,
  getDisplayText = (value: T) => value as string,
  onValueChange,
  compareBy: compareFn = (a, b) => a === b,
  placeholder,
  children,
  onSearchTextChanged,
  className,
  ...props
}: NidComboboxSingleProps<T>) {
  const [query, setQuery] = useState("");

  const onSetQuery = (value: string) => {
    setQuery(value);
    if (onSearchTextChanged) {
      onSearchTextChanged(value);
    }
  };

  return (
    <Combobox
      as="div"
      className={classNames(
        "w-full rounded-lg border border-solid border-gray-100",
        className
      )}
      value={value}
      onChange={onValueChange}
      by={compareFn as any}
    >
      <Float
        as="div"
        className="relative w-full"
        placement="bottom-start"
        flip={0}
        offset={4}
        transform
        adaptiveWidth
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
        onHide={() => setQuery("")}
      >
        <div className="relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-th-primary-400 sm:text-sm">
          <Combobox.Input
            placeholder={placeholder ?? "Suchen"}
            className="min-h-[40px] w-full border-none py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:ring-0"
            displayValue={getDisplayText}
            onChange={(event) => onSetQuery(event.target.value)}
            data-cy="combobox-input"
          />
          <Combobox.Button
            className="absolute inset-y-0 right-0 flex items-center pr-2"
            data-cy="combobox-button"
          >
            <ChevronUpDownIcon
              className="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </Combobox.Button>
        </div>

        <Combobox.Options className="relative z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
          <NidComboboxOptions
            values={values}
            query={query}
            setQuery={onSetQuery}
            getDisplayText={getDisplayText}
            getSearchText={getSearchText}
          >
            <NidComboboxOptions.OptionsPrefix>
              {React.Children.map(children, (child) => {
                if (
                  React.isValidElement(child) &&
                  child.type === NidComboboxSingle.OptionsPrefix
                ) {
                  return React.cloneElement(child);
                }
              })}
            </NidComboboxOptions.OptionsPrefix>
          </NidComboboxOptions>
        </Combobox.Options>
      </Float>
    </Combobox>
  );
}

function NidComboboxMulti<T>({
  value = [],
  values,
  getSearchText = (value: T) => value as string,
  getDisplayText = (value: T) => value as string,
  onValueChange = (_) => {},
  placeholder,
  compareBy: compareFn = (a, b) => a === b,
  children,
  onSearchTextChanged,
  className,
  ...props
}: NidComboboxMultipleProps<T>) {
  const [query, setQuery] = useState("");

  const removeValue = (removeValue: T) => {
    onValueChange(value.filter((v) => v !== removeValue));
  };

  const onSetQuery = (value: string) => {
    setQuery(value);
    if (onSearchTextChanged) {
      onSearchTextChanged(value);
    }
  };

  return (
    <Combobox
      as="div"
      className={classNames(
        "relative w-full rounded-lg border border-solid border-gray-100",
        className
      )}
      value={value}
      onChange={onValueChange as any}
      by={compareFn as any}
      multiple
    >
      <Float
        as="div"
        className="relative w-full"
        placement="bottom-start"
        flip={0}
        offset={4}
        transform
        adaptiveWidth
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
        onHide={() => setQuery("")}
      >
        <div className="relative flex w-full cursor-default overflow-hidden rounded-lg bg-white text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-th-primary-400 sm:text-sm">
          <div className="flex flex-[0_1_auto] flex-wrap">
            {value.map((value, i) => (
              <div
                className="my-1 ml-1 flex min-h-[32px] flex-[0_0_auto] items-center rounded-lg bg-th-primary-900 px-2 text-center text-white"
                key={`${getDisplayText(value)}_${i}`}
              >
                <span>{getDisplayText(value)}</span>
                <XMarkIcon
                  title="Entfernen"
                  onClick={() => removeValue(value)}
                  className="ml-1 h-5 w-5 hover:cursor-pointer"
                ></XMarkIcon>
              </div>
            ))}
          </div>
          <Combobox.Input
            placeholder={placeholder ?? "Suchen"}
            className="min-h-[40px] w-full min-w-[200px] flex-[1_1_200px] border-none py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 outline-none focus:ring-0"
            onChange={(event) => onSetQuery(event.target.value)}
          />
          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon
              className="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </Combobox.Button>
        </div>
        <Combobox.Options className="relative z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
          <NidComboboxOptions
            values={values}
            query={query}
            setQuery={onSetQuery}
            getDisplayText={getDisplayText}
            getSearchText={getSearchText}
          >
            <NidComboboxOptions.OptionsPrefix>
              {React.Children.map(children, (child) => {
                if (
                  React.isValidElement(child) &&
                  child.type === NidComboboxMulti.OptionsPrefix
                ) {
                  return React.cloneElement(child);
                }
              })}
            </NidComboboxOptions.OptionsPrefix>
          </NidComboboxOptions>
        </Combobox.Options>
      </Float>
    </Combobox>
  );
}

const OptionsPrefix: React.FC<{ children: ReactNode }> = ({ children }) => {
  return <>{children}</>;
};

NidComboboxMulti.OptionsPrefix = OptionsPrefix;
NidComboboxSingle.OptionsPrefix = OptionsPrefix;
NidComboboxOptions.OptionsPrefix = OptionsPrefix;

export { NidComboboxSingle, NidComboboxMulti };
